import type {
    Domain,
    DomainMetadata,
} from "@amzn/d16-g-cdb-publisher-portal-typescript-client";
import { Alert, Link, Text } from "@amzn/storm-ui";
import { useAsync, useMountEffect, useUnmountEffect } from "@react-hookz/web";
import React, { useMemo } from "react";
import { useParams } from "react-router";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { useStore } from "zustand";

import { getCdbApiClient } from "../../api/cdbApi/cdbApi";
import {
    getPublisherPortalApiClient,
    getTemplateDownloadLink,
} from "../../api/publisherPortalApi/publisherPortalApi";
import { LoadingSpinner } from "../../components/LoadingSpinner";
import { CardContainer } from "../../components/common/styled.components";
import { ActionBar } from "../../components/createOnDemandInsight/ActionBar";
import { SettingsCard } from "../../components/createOnDemandInsight/SettingsCard";
import { DeepLinkAccountPickerSection } from "../../components/customAccountPicker/DeepLinkAccountPickerSection";
import { TemplateAccessDeniedAlert } from "../../components/customAccountPicker/TemplateAccessDeniedAlert";
import { AI_ENHANCED_INSIGHT_DIMENSION } from "../../constants/fixedDimensions";
import { useTemplateAccessValidation } from "../../hooks/misc/useTemplateAccessValidation";
import { useAsyncErrorStatusNotification } from "../../hooks/notifications/useAsyncErrorStatusNotification";
import { ctiStore } from "../../store/cti";
import { isLoading } from "../../utils/api.utils";
import { formatDate } from "../../utils/date.utils";
import {
    getAdvertiserId,
    QueryParam,
    isAccountAgnosticId,
} from "../../utils/permission.utils";
import { getValueFromSearchParams } from "../../utils/window.utils";

const ColumnFlex = styled.div`
    display: flex;
    flex-direction: column;
    gap: 20px;
`;

const FIXED_CONTAINERS = ["insighthub-alert"];

export interface ValidationCheck {
    status: boolean;
    errorMessage: string;
}

const handleTemplateAccess = async (
    data: Domain,
    hasAdvertiserId: string | null,
    templateIdentifier: string,
    validateTemplateAccess: (
        data: Domain,
        templateId: string,
        advertiserId: string,
    ) => Promise<{ hasAccess: boolean; reason?: string }>,
    setShowDeepLinkPicker: (show: boolean) => void,
    setShowAccessDenied: (show: boolean) => void,
    setAccessDeniedReason: (reason: string) => void,
    setCti: (cti: any) => void,
    setDomain: (domain: Domain) => void,
): Promise<Domain | null> => {
    setDomain(data);
    if (data?.metadata?.advertiserIdScoped) {
        const hasRealAccountId = hasAdvertiserId && !isAccountAgnosticId();

        if (!hasRealAccountId) {
            setShowDeepLinkPicker(true);
        } else if (hasAdvertiserId) {
            const validationResult = await validateTemplateAccess(
                data,
                templateIdentifier,
                hasAdvertiserId,
            );

            if (!validationResult.hasAccess) {
                setShowAccessDenied(true);
                setAccessDeniedReason(
                    validationResult.reason || "Access denied",
                );
            }
        }
    }
    if (data?.cti) setCti(data.cti);
    return data;
};

const getFixedDimensions = (metadata: DomainMetadata | undefined): any[] => {
    const fixedDimensions: any[] = [];

    if (metadata?.enableAiEnhancedInsights) {
        fixedDimensions.push(AI_ENHANCED_INSIGHT_DIMENSION);
    }

    return fixedDimensions;
};

export const CreateOnDemandInsightContainer: React.FunctionComponent = () => {
    const { domainId: domainIdentifier = "" } = useParams();
    const { templateId: templateIdentifier = "" } = useParams();
    const navigate = useNavigate();
    const [setCti, resetCti] = useStore(ctiStore, (state) => [
        state.setCti,
        state.resetCti,
    ]);

    const [domain, setDomain] = React.useState<Domain>();
    const hasAdvertiserId = getValueFromSearchParams(QueryParam.SALESFORCE_ID);
    const [showDeepLinkPicker, setShowDeepLinkPicker] = React.useState(false);
    const [showAccessDenied, setShowAccessDenied] = React.useState(false);
    const [accessDeniedReason, setAccessDeniedReason] =
        React.useState<string>("");

    const { validateTemplateAccess } = useTemplateAccessValidation();

    const [hydrationPayload, hydrationPayloadAction] = useAsync(async () => {
        const generatedInsightId =
            getValueFromSearchParams("getPayloadFromInsight") ?? "";

        if (generatedInsightId) {
            const { data } = await (
                await getCdbApiClient()
            ).getGeneratedInsight({
                advertiserId: getAdvertiserId(),
                executionId: generatedInsightId,
                withFullMetadata: true,
            });
            return data?.metadata;
        }
    });

    const [domainState, domainAction] = useAsync(async () => {
        const { data } = await (
            await getPublisherPortalApiClient()
        ).getDomain({
            domainIdentifier,
        });

        if (!data) return null;

        return await handleTemplateAccess(
            data,
            hasAdvertiserId ?? null,
            templateIdentifier,
            validateTemplateAccess,
            setShowDeepLinkPicker,
            setShowAccessDenied,
            setAccessDeniedReason,
            setCti,
            setDomain,
        );
    });
    useAsyncErrorStatusNotification(domainState);

    useMountEffect(async () => {
        void domainAction.execute();
        void hydrationPayloadAction.execute();
    });

    useUnmountEffect(() => {
        resetCti();
    });

    const dimensionSections: any = React.useMemo(() => {
        const baseDimensions = (domain?.dimensions as any[]) || [];
        const fixedDimensions = getFixedDimensions(domain?.metadata);

        if (fixedDimensions.length > 0) {
            return [...baseDimensions, fixedDimensions];
        }

        return baseDimensions;
    }, [domain]);

    const template = React.useMemo(() => {
        return domain?.templates?.find((t) => t.id === templateIdentifier);
    }, [domain, templateIdentifier]);

    const templateDownloadLink = getTemplateDownloadLink(
        template?.s3Location?.key,
    );

    const templateInfo: Record<string, string> = {
        name: template?.name ?? "",
        description: template?.description ?? "",
        methodology: template?.methodology ?? "",
        lastUpdatedTimestamp: formatDate(template?.lastUpdatedTimestamp),
    };

    const handleAccountSelect = (accountId: string) => {
        setShowDeepLinkPicker(false);
    };

    const handlePickerCancel = () => {
        setShowDeepLinkPicker(false);
        // Redirect to IHub home
        navigate("/");
    };

    const handlePickerClose = () => {
        setShowDeepLinkPicker(false);
        // Redirect to ihub home with account agnostic view
        navigate("/");
    };

    const publisherPortalValidationCheckFailed = useMemo(() => {
        // Don't run validation while still loading
        if (
            domainState.status === "loading" ||
            domainState.status === "not-executed"
        ) {
            return null;
        }

        // Don't run validation while still loading
        if (isLoading(domainState.status)) {
            return null;
        }

        // Don't validate template if we should show deep link account picker
        if (showDeepLinkPicker) {
            return null;
        }

        // If domain is undefined after loading, show error
        if (!domainState) {
            return {
                status: true,
                errorMessage:
                    "Oops! This URL doesn't lead to any resources on Insights Hub. The content may have been moved, deleted, or the link might be incorrect. Please verify the URL or go back to iHub to find what you're looking for.",
            };
        }

        const validations: ValidationCheck[] = [
            {
                status: template === undefined,
                errorMessage:
                    "Oops! This URL doesn't lead to any resources on Insights Hub. The content may have been moved, deleted, or the link might be incorrect. Please verify the URL or go back to iHub to find what you're looking for.",
            },
            {
                status:
                    domain?.enabled === false || template?.enabled === false,
                errorMessage:
                    "This resource has been disabled or archived and is currently unavailable. Please contact the content owners for assistance or to request access.",
            },
        ];
        return validations.find(({ status }) => status);
    }, [domain, template, domainState.status, showDeepLinkPicker]);

    // All hooks must be called before any conditional returns

    // Show deep link account picker for advertiser-scoped templates without sfid
    if (showDeepLinkPicker && domain) {
        return (
            <DeepLinkAccountPickerSection
                domain={domain}
                templateId={templateIdentifier}
                templateName={template?.name ?? ""}
                domainIdentifier={domainIdentifier}
                onAccountSelect={handleAccountSelect}
                onCancel={handlePickerCancel}
                onClose={handlePickerClose}
            />
        );
    }

    // Show access denied message for users with invalid existing sfId from url
    if (showAccessDenied && template && domain) {
        return (
            <TemplateAccessDeniedAlert
                onTryDifferentAccount={() => {
                    setShowAccessDenied(false);
                    setShowDeepLinkPicker(true);
                }}
            />
        );
    }

    // Show loading state while domain is being fetched
    if (isLoading(domainState.status)) {
        return (
            <CardContainer>
                <LoadingSpinner />
            </CardContainer>
        );
    }

    // Show validation errors
    if (publisherPortalValidationCheckFailed) {
        return (
            <CardContainer>
                <Alert variant="error" header="Something went wrong">
                    <Text>
                        {publisherPortalValidationCheckFailed.errorMessage}
                    </Text>
                </Alert>
            </CardContainer>
        );
    }

    return (
        <section>
            <ActionBar
                domainIdentifier={domainIdentifier}
                templateIdentifier={templateIdentifier}
                templateName={template?.name ?? ""}
            />
            <CardContainer>
                <ColumnFlex>
                    {getValueFromSearchParams("getPayloadFromInsight") && (
                        <Alert
                            variant="info"
                            header="[New Feature] Clone Insight"
                        >
                            <Text>
                                This feature enables you to copy the inputs from
                                a previously packaged insight and resubmit with
                                new inputs. While most fields will be
                                automatically populated with your original
                                inputs, please note that some fields may need to
                                be re-entered for technical or security reasons.
                            </Text>
                            <Text>
                                <Link href="https://quip-amazon.com/92u9AS3edlEz/IHUB-Clone-Insight">
                                    Learn about it here
                                </Link>
                            </Text>
                        </Alert>
                    )}
                    {hydrationPayload.status === "success" ? (
                        <SettingsCard
                            templatePublisherPortalLink={templateDownloadLink}
                            dimensionSections={dimensionSections}
                            loading={isLoading(domainState.status)}
                            dimensionMetadata={{ domainIdentifier }}
                            hydrationPayload={hydrationPayload.result as any}
                            templateInfo={templateInfo}
                        />
                    ) : (
                        hydrationPayload.status === "error" && (
                            <>
                                <Alert
                                    variant="error"
                                    header="Edit Request Failed"
                                >
                                    <Text>
                                        Could not fetch metadata for insight
                                        input.
                                    </Text>
                                </Alert>
                                <SettingsCard
                                    templatePublisherPortalLink={
                                        templateDownloadLink
                                    }
                                    dimensionSections={dimensionSections}
                                    loading={isLoading(domainState.status)}
                                    dimensionMetadata={{ domainIdentifier }}
                                />
                            </>
                        )
                    )}
                </ColumnFlex>
            </CardContainer>
        </section>
    );
};
